import { createEvent, createStore, restore, sample } from 'effector'
import { fx } from 'src/libs'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { getBudgetErrorNotification } from 'src/pages/budget/errorNotification'
import {
  AddArticlePayload,
  ModalDeleteArticleItem,
  ModalArticle,
  ResponseAddArticle,
  ModalDeleteArticle,
  UpdateArticlePayload,
  DeleteArticleItemPayload,
  UpdateArticleItemPayload,
} from 'src/pages/budget/budgetCategories/types'

import {
  AddArticleItemPayload,
  ResponseArticleItem,
} from 'src/pages/budget/types'
import {
  addArticleItemFx,
  getArticleListFx,
  getTeamListFx,
  getUsersFx,
} from 'src/pages/budget/model'
import { ArticleType } from 'src/pages/budget/types'
import { Request } from 'src/libs/request'
import { createGate } from 'effector-react'

export const BudgetCategoriesGate = createGate()

export const openModalArticle = createEvent<ModalArticle>()
export const closeModalArticle = createEvent()
export const toggleModalArticleForEdit = createEvent()

export const openModalDeleteArticle = createEvent<ModalDeleteArticle>()
export const closeModalDeleteArticle = createEvent()

export const openModalDeleteArticleItem = createEvent<ModalDeleteArticleItem>()
export const closeModalDeleteArticleItem = createEvent()

export const submitDeleteArticle = createEvent<{ articleId: number }>()
export const submitAddArticle = createEvent<AddArticlePayload>()
export const submitUpdateArticle = createEvent<UpdateArticlePayload>()

export const submitAddArticleItem = createEvent<AddArticleItemPayload>()
export const submitDeleteArticleItem = createEvent<DeleteArticleItemPayload>()
export const submitUpdateArticleItem = createEvent<UpdateArticleItemPayload>()


export const addArticleFx = fx<AddArticlePayload, ResponseAddArticle>({
  method: 'POST',
  url: '/budget/article',
})

export const deleteArticleFx = fx<{ url: string; articleId: number }, any>({
  method: 'DELETE',
  url: '/budget/article',
})

export const updateArticleFx = fx<UpdateArticlePayload, ResponseAddArticle>({
  method: 'PUT',
  url: '/budget/article',
})

export const deleteArticleItemFx = fx<
  DeleteArticleItemPayload,
  ResponseArticleItem
>({
  method: 'DELETE',
  url: '/budget/article_item',
})

export const updateArticleItemFx = fx<
  Omit<UpdateArticleItemPayload, 'articleItemId'>,
  ResponseArticleItem
>({
  method: 'PUT',
  url: '/budget/article_item',
})

export const $articleList = restore<ArticleType[]>(
  getArticleListFx.doneData.map((a) => a.body.items),
  [],
)

export const $modalArticle = createStore<ModalArticle | null>(null)
  .on(openModalArticle, (_, payload) => payload)
  .on(toggleModalArticleForEdit, (state) => {
    if (!state) return null
    return { ...state, mode: 'edit' }
  })
  .reset(closeModalArticle)

export const $modalDeleteArticle = createStore<ModalDeleteArticle | null>(null)
  .on(openModalDeleteArticle, (_, payload) => payload)
  .reset(closeModalDeleteArticle)

export const $modalDeleteArticleItem =
  createStore<ModalDeleteArticleItem | null>(null)
    .on(openModalDeleteArticleItem, (_, payload) => payload)
    .reset(closeModalDeleteArticleItem)

sample({
  clock: BudgetCategoriesGate.open,
  fn: () => ({}),
  target: [getArticleListFx, getUsersFx, getTeamListFx], // ceoTeam
})

sample({
  clock: submitAddArticle,
  fn: (body) => ({ body }),
  target: addArticleFx,
})

sample({
  clock: submitDeleteArticle,
  fn: ({ articleId }) => ({
    url: `/budget/article/${articleId}`,
    articleId,
  }),
  target: deleteArticleFx,
})

sample({
  clock: submitUpdateArticle,
  fn: ({ articleId, name, description, users }) => {
    return {
      url: `/budget/article/${articleId}`,
      body: { name, description, users },
    } as Request<UpdateArticlePayload>
  },
  target: updateArticleFx,
})

sample({
  clock: submitAddArticleItem,
  fn: (body) => ({ body }),
  target: addArticleItemFx,
})

sample({
  clock: submitDeleteArticleItem,
  fn: ({ articleItemId }) => ({
    url: `/budget/article_item/${articleItemId}`,
    articleItemId,
  }),
  target: deleteArticleItemFx,
})

sample({
  clock: submitUpdateArticleItem,
  fn: ({ articleItemId, name, description }) => {
    return {
      url: `/budget/article_item/${articleItemId}`,
      body: { name, description },
    }
  },
  target: updateArticleItemFx,
})

sample({
  clock: addArticleFx.doneData,
  source: $articleList,
  fn: (articleList, { body: { id, number, name, description, users } }) => {
    return [
      ...articleList,
      {
        articleId: id,
        number: number,
        name: name,
        description: description,
        users: users,
        items: [],
      },
    ]
  },
  target: $articleList,
})

sample({
  clock: deleteArticleFx.done,
  source: $articleList,
  fn: (articleList, { params }) => {
    const { articleId } = params as { url: string; articleId: number }
    return articleList.filter((item) => item.articleId !== articleId)
  },
  target: $articleList,
})

sample({
  clock: updateArticleFx.doneData,
  source: $articleList,
  fn: (articleList, { body: { id, number, name, description, users } }) => {
    return articleList.map((article) =>
      article.articleId !== id
        ? article
        : {
            articleId: id,
            number,
            name,
            description,
            users,
            items: article.items,
          },
    )
  },
  target: $articleList,
})

sample({
  clock: addArticleItemFx.doneData,
  source: $articleList,
  fn: (
    articleList,
    { body: { articleId, id, number, fullNumber, name, description } },
  ) => {
    return articleList.map((article) =>
      article.articleId === articleId
        ? {
            ...article,
            items: [
              ...article.items,
              { id, number, fullNumber, name, description },
            ],
          }
        : article,
    )
  },
  target: $articleList,
})

sample({
  clock: deleteArticleItemFx.done,
  source: $articleList,
  fn: (articleList, { params, result }) => {
    const { articleItemId } = params as { url: string; articleItemId: number }
    const articleId = result.body.articleId

    return articleList.map((article) =>
      article.articleId === articleId
        ? {
            ...article,
            items: article.items.filter((it) => it.id !== articleItemId),
          }
        : article,
    )
  },
  target: $articleList,
})

sample({
  clock: updateArticleItemFx.doneData,
  source: $articleList,
  fn: (
    articleList,
    { body: { articleId, id, number, fullNumber, name, description } },
  ) => {
    return articleList.map((article) =>
      article.articleId === articleId
        ? {
            ...article,
            items: article.items.map((item) =>
              item.id === id
                ? { id, number, fullNumber, name, description }
                : item,
            ),
          }
        : article,
    )
  },
  target: $articleList,
})

sample({
  clock: [
    deleteArticleFx.failData,
    addArticleFx.failData,
    getArticleListFx.failData,
    updateArticleFx.failData,
    addArticleItemFx.failData,
    deleteArticleItemFx.failData,
    updateArticleItemFx.failData,
  ],
  fn: getBudgetErrorNotification,
  target: showNotification,
})

sample({
  clock: addArticleFx.doneData,
  fn: (params): NotificationPayload => {
    const nameArticle = params?.body?.name ?? ''
    return {
      type: 'success',
      description: `Статья бюджета ${nameArticle} успешно сохранена`,
    }
  },
  target: showNotification,
})

sample({
  clock: deleteArticleFx.doneData,
  fn: (params): NotificationPayload => {
    const nameArticle = params?.body?.name ?? ''
    return {
      type: 'success',
      description: `Статья бюджета ${nameArticle} успешно удалена`,
    }
  },
  target: showNotification,
})

sample({
  clock: updateArticleFx.doneData,
  fn: (params): NotificationPayload => {
    const nameArticle = params?.body?.name ?? ''
    return {
      type: 'success',
      description: `Статья бюджета ${nameArticle} успешно изменена`,
    }
  },
  target: showNotification,
})

sample({
  clock: addArticleItemFx.doneData,
  fn: ({ body }): NotificationPayload => {
    const nameArticleItem = `${body?.fullNumber ?? ''} ${
      body?.name ?? ''
    }`.trim()
    const nameArticle = `${body?.article?.number ?? ''} ${
      body?.article?.name ?? ''
    }`.trim()

    return {
      type: 'success',
      description: `Пункт ${nameArticleItem} успешно сохранён в статью бюджета ${nameArticle}`,
    }
  },
  target: showNotification,
})

sample({
  clock: deleteArticleItemFx.doneData,
  fn: (params): NotificationPayload => {
    const nameArticleItem = params?.body?.name ?? ''
    const nameArticle = params?.body?.article.name ?? ''
    return {
      type: 'success',
      description: `Пункт ${nameArticleItem} успешно удалён из статьи бюджета ${nameArticle}`,
    }
  },
  target: showNotification,
})

sample({
  clock: updateArticleItemFx.doneData,
  fn: (params): NotificationPayload => {
    const nameArticleItem = params?.body?.name ?? ''
    const nameArticle = params?.body?.article.name ?? ''
    return {
      type: 'success',
      description: `Пункт ${nameArticleItem} успешно изменён в статье бюджета ${nameArticle}`,
    }
  },
  target: showNotification,
})
